import { Brand, createBranded } from '@deltasierra/type-utilities';
import {
    FullAuditable,
    FullAuditableKeys,
    HasId,
    Omit,
    CurrencyCode,
    CurrencyNumber,
    Url,
    PathBuilder,
    ApiRoute,
    HttpMethod,
} from '../common';
import { LocationId } from '../location';
import { AgencyApiParams, AgencyId } from '../agency';
import { PrintProviderDtoWithBasicClientInfo } from './PrintProvider';
import { GetAllSKUsForSKUGroupDto, GetMappedSKUFormatOptionsResponse, PrintProviderSKUDto } from './PrintProviderSKU';

export type PrintProviderSKUGroupId = Brand<number, 'PrintProviderSKUGroupId'>;
export const PrintProviderSKUGroupId = createBranded<PrintProviderSKUGroupId>();

export interface PrintProviderSKUGroup extends HasId<PrintProviderSKUGroupId>, FullAuditable {
    name: string;
    paymentUrl: Url | null;
    paymentGateway: 'bluepay' | 'four51' | 'stripe' | null;
    orderInformation: string | null;
    agencyId: AgencyId;
    defaultPriceCurrency: CurrencyCode;
    shippingFeePriceAmountInCents: CurrencyNumber;
}

export type BasicSKUGroup = Pick<PrintProviderSKUGroup, 'id' | 'name'>;

export type FilteredSKUGroup = Omit<PrintProviderSKUGroup, FullAuditableKeys> & {
    printProviderSKUs: PrintProviderSKUDto[];
};

export type BasicSKUGroupWithProvidersCount = BasicSKUGroup & {
    printProvidersCount?: number;
};

export type IUpdatePrintProviderSKUGroupBody = Omit<ICreatePrintProviderSKUGroupBody, 'paymentGateway'>;

export interface GetPrintProviderSKUGroupQuery {
    builderTemplateFormatId: number;
    locationId: LocationId;
}

export interface GetPrintProviderSKUGroupResponse {
    skuGroup: FilteredSKUGroup | null;
}

export interface ICreatePrintProviderSKUGroupBody {
    name: string;
    paymentUrl: Url | null;
    paymentGateway: 'bluepay' | 'four51' | 'stripe' | null;
    orderInformation: string | null;
    defaultPriceCurrency: CurrencyCode;
    shippingFeePriceAmountInCents: CurrencyNumber;
}

interface AgencySKUGroupParams {
    agencyId: AgencyId;
    printProviderSKUGroupId: PrintProviderSKUGroupId;
}

function SKU_GROUPS_COLLECTION_API_ROOT<T extends AgencyApiParams>(path: PathBuilder<T>) {
    return path.literal('agencies').param('agencyId').literal('printProviderSKUGroups');
}

function SKU_GROUP_API_ROOT<T extends AgencySKUGroupParams>(path: PathBuilder<T>) {
    return SKU_GROUPS_COLLECTION_API_ROOT(path).param('printProviderSKUGroupId');
}

export const PrintProviderSKUGroupsApi = {
    createSKUGroup: new ApiRoute<
        BasicSKUGroupWithProvidersCount,
        AgencyApiParams,
        ICreatePrintProviderSKUGroupBody,
        void
    >(HttpMethod.Post, SKU_GROUPS_COLLECTION_API_ROOT),
    deleteSKUGroup: new ApiRoute<void, AgencySKUGroupParams, void, void>(HttpMethod.Delete, SKU_GROUP_API_ROOT),
    getMappedSKUFormatOptions: new ApiRoute<GetMappedSKUFormatOptionsResponse[], AgencySKUGroupParams, void, void>(
        HttpMethod.Get,
        path => SKU_GROUP_API_ROOT(path).literal('unavailableFormatOptions'),
    ),
    getPrintProviders: new ApiRoute<PrintProviderDtoWithBasicClientInfo[], AgencyApiParams, void, void>(
        HttpMethod.Get,
        path => SKU_GROUPS_COLLECTION_API_ROOT(path).literal('printProviders'),
    ),
    getSKUGroup: new ApiRoute<PrintProviderSKUGroup, AgencySKUGroupParams, void, void>(
        HttpMethod.Get,
        SKU_GROUP_API_ROOT,
    ),
    getSKUGroupName: new ApiRoute<PrintProviderSKUGroup, AgencySKUGroupParams, void, void>(HttpMethod.Get, path =>
        SKU_GROUP_API_ROOT(path).literal('name'),
    ),
    getSKUGroupSKUs: new ApiRoute<GetAllSKUsForSKUGroupDto[], AgencySKUGroupParams, void, void>(HttpMethod.Get, path =>
        SKU_GROUP_API_ROOT(path).literal('SKUs'),
    ),
    getSKUGroups: new ApiRoute<BasicSKUGroupWithProvidersCount[], AgencyApiParams, void, void>(
        HttpMethod.Get,
        SKU_GROUPS_COLLECTION_API_ROOT,
    ),
    updateSKUGroup: new ApiRoute<PrintProviderSKUGroup, AgencySKUGroupParams, IUpdatePrintProviderSKUGroupBody, void>(
        HttpMethod.Patch,
        SKU_GROUP_API_ROOT,
    ),
};
